import { API_BASE } from "@/lib/api";

// Inline media tokens in a news body, each on its own line: [image:URL|caption]  [video:URL|caption]
export const MEDIA_RX = /^\[(image|video):\s*(\S+?)\s*(?:\|(.*))?\]$/;

const abs = (u) => (u && u.startsWith("/api/") ? `${API_BASE}${u}` : u);

export const videoEmbed = (url) => {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube-nocookie.com/embed/${yt[1]}?rel=0` };
  if (/player\.vimeo\.com\/video\//.test(url)) return { kind: "iframe", src: url };
  const vm = url.match(/vimeo\.com\/(?:.*\/)?(\d{6,})/);
  if (vm) return { kind: "iframe", src: `https://player.vimeo.com/video/${vm[1]}?dnt=1&title=0&byline=0&portrait=0` };
  return { kind: "file", src: abs(url) };
};

const Media = ({ type, url, caption, i }) => {
  const v = type === "video" ? videoEmbed(url) : null;
  return (
    <figure className="my-5" data-testid={`news-inline-${type}-${i}`}>
      {type === "image" ? (
        <img src={abs(url)} alt={caption || ""} loading="lazy" className="w-full rounded-xl border border-[#273042] bg-black" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : v.kind === "iframe" ? (
        <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-[#273042] bg-black">
          <iframe src={v.src} title={caption || "Video"} className="absolute inset-0 w-full h-full" allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
        </div>
      ) : (
        <video src={v.src} controls playsInline preload="metadata" className="w-full rounded-xl border border-[#273042] bg-black" />
      )}
      {caption && <figcaption className="text-xs text-slate-400 mt-2 italic">{caption}</figcaption>}
    </figure>
  );
};

export const NewsBody = ({ text }) => {
  const blocks = [];
  let para = [];
  const flush = () => { if (para.length) { blocks.push({ t: "p", v: para.join("\n") }); para = []; } };
  (text || "").split("\n").forEach((line) => {
    const m = line.trim().match(MEDIA_RX);
    if (m) { flush(); blocks.push({ t: m[1], url: m[2], caption: (m[3] || "").trim() }); }
    else para.push(line);
  });
  flush();
  return (
    <div className="mt-4" data-testid="news-body">
      {blocks.map((b, i) => b.t === "p"
        ? (b.v.trim() ? <p key={i} className="text-slate-300 leading-relaxed whitespace-pre-line mb-3">{b.v.trim()}</p> : null)
        : <Media key={i} i={i} type={b.t} url={b.url} caption={b.caption} />)}
    </div>
  );
};

export const AuthorCard = ({ n }) => (
  <div className="mt-8 pt-5 border-t border-[#273042] flex items-center gap-4" data-testid="news-author-card">
    <img src={abs(n.authorPhoto) || "/bb-logo.png"} alt={n.authorName} className="w-14 h-14 rounded-full object-cover border-2 border-[#E11D2A] bg-white shrink-0"
      onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Written by</div>
      <div className="text-white font-bold truncate" data-testid="news-author-name">{n.authorName}</div>
      <div className="text-xs text-slate-400" data-testid="news-author-role">{n.authorRole}{n.team ? ` · ${n.team}` : ""}</div>
      {n.publishedAt && <div className="text-[11px] text-slate-500 mt-0.5" data-testid="news-author-date">Published {new Date(n.publishedAt).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}</div>}
    </div>
  </div>
);
